import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Star, Eye, Shield, Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import { Vendor, CreateVendorForm, Category, VendorSearchDto, VendorComplianceStatus } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import DataGrid from '../components/grid/DataGrid';
import Modal from '../components/ui/Modal';
import VendorForm from '../components/forms/VendorForm';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import VendorComplianceIcon from '../components/vendors/VendorComplianceIcon';
import VendorCoverageSearch, { DEFAULT_COVERAGE_RADIUS, getCoverageZip } from '../components/vendors/VendorCoverageSearch';
import { formatPhoneNumber } from '../utils/formatters';
import { normalizeComplianceStatus } from '../utils/vendorCompliance';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const parseRadiusParam = (value: string | null): number => {
  const parsed = value ? parseInt(value, 10) : DEFAULT_COVERAGE_RADIUS;
  return [25, 50, 75, 100, 200].includes(parsed) ? parsed : DEFAULT_COVERAGE_RADIUS;
};

const mapCoverageVendor = (vendor: VendorSearchDto, categoryLookup: Category[]): Vendor => ({
  id: vendor.id,
  companyId: 0,
  name: vendor.name,
  address: vendor.address,
  city: vendor.city,
  state: vendor.state,
  zipCode: vendor.zipCode,
  phone: vendor.phone,
  email: vendor.email,
  website: vendor.website,
  notes: vendor.notes,
  rank: vendor.rank,
  rating: vendor.rating,
  createdDate: '',
  isActive: vendor.isActive,
  distance: vendor.distance,
  complianceStatus: vendor.complianceStatus,
  complianceStatusLabel: vendor.complianceStatusLabel,
  categories: (vendor.categories || []).map((name) =>
    categoryLookup.find((category) => category.name === name) || {
      id: 0,
      name,
      isActive: true,
      createdAt: '',
    }
  ),
});

const matchesCoverageCompliance = (vendor: Vendor, filter: string): boolean => {
  const status = normalizeComplianceStatus(vendor.complianceStatus, vendor.complianceStatusLabel);
  const label = (vendor.complianceStatusLabel || '').toLowerCase().replace(/[-_\s]/g, '');

  switch (filter) {
    case 'pendingreview':
      return label.includes('review') || label.includes('pending');
    case 'compliant':
      return status === VendorComplianceStatus.Compliant && !label.includes('review');
    case 'expiring':
      return status === VendorComplianceStatus.ExpiringSoon;
    case 'noncompliant':
      return status === VendorComplianceStatus.NonCompliant;
    default:
      return true;
  }
};

const getCoverageErrorMessage = (error: unknown): string => {
  const axiosError = error as { response?: { data?: { message?: string } | string } };
  const data = axiosError.response?.data;
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  if (data && typeof data === 'object' && data.message) {
    return data.message;
  }
  return 'Could not search this zip code.';
};

const sortCoverageVendors = (vendors: Vendor[], field: string, direction: 'asc' | 'desc'): Vendor[] => {
  const dir = direction === 'asc' ? 1 : -1;

  return [...vendors].sort((a, b) => {
    switch (field) {
      case 'distance':
        return ((a.distance ?? 0) - (b.distance ?? 0)) * dir;
      case 'name':
        return a.name.localeCompare(b.name) * dir;
      case 'city':
        return (a.city || '').localeCompare(b.city || '') * dir;
      case 'state':
        return (a.state || '').localeCompare(b.state || '') * dir;
      case 'zipCode':
        return (a.zipCode || '').localeCompare(b.zipCode || '') * dir;
      case 'phone':
        return (a.phone || '').localeCompare(b.phone || '') * dir;
      case 'rating':
        return ((a.rating ?? 0) - (b.rating ?? 0)) * dir;
      case 'isActive':
        return (Number(a.isActive) - Number(b.isActive)) * dir;
      default:
        return ((a.distance ?? 0) - (b.distance ?? 0)) * dir;
    }
  });
};

const VendorsPage: React.FC = () => {
  const { token, user } = useAuth();
  const { hasFeature } = useSubscription();
  const hasVendorCompliance = hasFeature('hasVendorCompliance');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check if this is a system admin session
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';
  
  // Initialize state from URL params or defaults
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const size = searchParams.get('pageSize');
    return size ? parseInt(size, 10) : 10;
  });
  const [sortField, setSortField] = useState<string>(() => {
    if (searchParams.get('sortField')) return searchParams.get('sortField') as string;
    return searchParams.get('zip') ? 'distance' : 'name';
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const dir = searchParams.get('sortDirection');
    return (dir === 'asc' || dir === 'desc') ? dir : 'asc';
  });
  const [complianceFilter, setComplianceFilter] = useState<string>(() => searchParams.get('compliance') || 'all');
  const [coverageZip, setCoverageZip] = useState(() => searchParams.get('zip') || '');
  const [coverageRadius, setCoverageRadius] = useState(() => parseRadiusParam(searchParams.get('radius')));
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [formError, setFormError] = useState<string>('');
  const isUpdatingFromState = useRef(false);

  // Sync state from URL params when they change externally (e.g., browser back/forward)
  useEffect(() => {
    if (isUpdatingFromState.current) {
      isUpdatingFromState.current = false;
      return;
    }

    const urlSearch = searchParams.get('search') || '';
    const urlPage = searchParams.get('page') ? parseInt(searchParams.get('page')!, 10) : 1;
    const urlPageSize = searchParams.get('pageSize') ? parseInt(searchParams.get('pageSize')!, 10) : 10;
    const urlSortField = searchParams.get('sortField')
      || (searchParams.get('zip') ? 'distance' : 'name');
    const urlSortDirection = (searchParams.get('sortDirection') === 'asc' || searchParams.get('sortDirection') === 'desc') 
      ? searchParams.get('sortDirection') as 'asc' | 'desc' 
      : 'asc';
    const urlCompliance = searchParams.get('compliance') || 'all';
    const urlCoverageZip = searchParams.get('zip') || '';
    const urlCoverageRadius = parseRadiusParam(searchParams.get('radius'));

    setSearchTerm(urlSearch);
    setCurrentPage(urlPage);
    setPageSize(urlPageSize);
    setSortField(urlSortField);
    setSortDirection(urlSortDirection);
    setComplianceFilter(urlCompliance);
    setCoverageZip(urlCoverageZip);
    setCoverageRadius(urlCoverageRadius);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Update URL params when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (pageSize !== 10) params.set('pageSize', pageSize.toString());
    if (sortField !== 'name') params.set('sortField', sortField);
    if (sortDirection !== 'asc') params.set('sortDirection', sortDirection);
    if (complianceFilter !== 'all') params.set('compliance', complianceFilter);
    if (coverageZip) params.set('zip', coverageZip);
    if (getCoverageZip(coverageZip)) params.set('radius', coverageRadius.toString());
    
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    
    // Only update if params actually changed
    if (currentParams !== newParams) {
      isUpdatingFromState.current = true;
      setSearchParams(params, { replace: true });
    }
  }, [searchTerm, currentPage, pageSize, sortField, sortDirection, complianceFilter, coverageZip, coverageRadius, setSearchParams, searchParams]);

  const coverageZipDigits = getCoverageZip(coverageZip);
  const isCoverageSearch = !!coverageZipDigits;

  // Queries
  const {
    data: vendorsResponse,
    isLoading,
    error
  } = useQuery({
    queryKey: ['vendors', currentPage, pageSize, searchTerm, sortField, sortDirection, complianceFilter],
    queryFn: () => apiService.getVendors(
      currentPage,
      pageSize,
      searchTerm,
      sortField,
      sortDirection,
      complianceFilter !== 'all' ? complianceFilter : undefined
    ),
    enabled: !!token && !isSystemAdmin && !isCoverageSearch
  });

  const {
    data: coverageResponse,
    isLoading: isCoverageLoading,
    error: coverageError
  } = useQuery({
    queryKey: ['vendors-coverage', coverageZipDigits, coverageRadius],
    queryFn: () => apiService.searchVendors(coverageZipDigits!, coverageRadius, []),
    enabled: !!token && !isSystemAdmin && isCoverageSearch
  });

  const {
    data: categoriesResponse
  } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(1, 100), // Get all categories for the form
    enabled: !!token && !isSystemAdmin
  });

  // Mutations
  const createVendorMutation = useMutation({
    mutationFn: (data: CreateVendorForm) => {
      console.log('Creating vendor with data:', data);
      return apiService.createVendor(data);
    },
    onSuccess: (response) => {
      console.log('Vendor creation response:', response);
      if (response.success && response.data) {
        toast.success(`Vendor "${response.data.name}" created successfully!`);
        setShowCreateModal(false);
        setFormError('');
        queryClient.invalidateQueries({ 
          queryKey: ['vendors'],
          exact: false 
        });
        queryClient.invalidateQueries({ queryKey: ['vendors-coverage'] });
      } else {
        toast.error(response.message || 'Failed to create vendor');
      }
    },
    onError: (error: any) => {
      console.error('Vendor creation error:', error);
      console.error('Error response:', error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.join(', ') || 'Error creating vendor';
      toast.error(errorMessage);
      setFormError(errorMessage);
    }
  });


  const deleteVendorMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteVendor(id),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(`Vendor "${selectedVendor?.name}" deleted successfully!`);
        setShowDeleteDialog(false);
        setSelectedVendor(null);
        queryClient.invalidateQueries({ 
          queryKey: ['vendors'],
          exact: false 
        });
        queryClient.invalidateQueries({ queryKey: ['vendors-coverage'] });
      } else {
        toast.error(response.message || 'Failed to delete vendor');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error deleting vendor';
      toast.error(errorMessage);
    }
  });

  // Handlers
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleCoverageZipChange = (zip: string) => {
    const wasActive = !!getCoverageZip(coverageZip);
    const isActive = !!getCoverageZip(zip);
    setCoverageZip(zip);
    setCurrentPage(1);
    if (isActive && !wasActive) {
      setSortField('distance');
      setSortDirection('asc');
    } else if (!isActive && wasActive && sortField === 'distance') {
      setSortField('name');
      setSortDirection('asc');
    }
  };

  const handleCoverageRadiusChange = (radius: number) => {
    setCoverageRadius(radius);
    setCurrentPage(1);
  };

  const handleCoverageClear = () => {
    setCoverageZip('');
    setCoverageRadius(DEFAULT_COVERAGE_RADIUS);
    setCurrentPage(1);
    if (sortField === 'distance') {
      setSortField('name');
      setSortDirection('asc');
    }
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setFormError('');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCreate = (data: CreateVendorForm) => {
    createVendorMutation.mutate(data);
  };

  const buildReturnUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (pageSize !== 10) params.set('pageSize', pageSize.toString());
    if (sortField !== 'name') params.set('sortField', sortField);
    if (sortDirection !== 'asc') params.set('sortDirection', sortDirection);
    if (coverageZip) params.set('zip', coverageZip);
    if (getCoverageZip(coverageZip)) params.set('radius', coverageRadius.toString());
    return params.toString() ? `/vendors?${params.toString()}` : '/vendors';
  }, [searchTerm, currentPage, pageSize, sortField, sortDirection, coverageZip, coverageRadius]);

  const handleEdit = useCallback((vendor: Vendor) => {
    const returnUrl = buildReturnUrl();
    navigate(`/vendors/${vendor.id}/edit?returnUrl=${encodeURIComponent(returnUrl)}`);
  }, [navigate, buildReturnUrl]);

  const handleCompliance = useCallback((vendor: Vendor) => {
    const returnUrl = buildReturnUrl();
    navigate(`/vendors/${vendor.id}/edit?tab=compliance&returnUrl=${encodeURIComponent(returnUrl)}`);
  }, [navigate, buildReturnUrl]);


  const handleDelete = useCallback((vendor: Vendor) => {
    setSelectedVendor(vendor);
    setShowDeleteDialog(true);
  }, []);

  const confirmDelete = () => {
    if (selectedVendor) {
      deleteVendorMutation.mutate(selectedVendor.id);
    }
  };

  // Render rating stars
  const renderRating = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={`${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600 dark:text-gray-400">{rating.toFixed(1)}</span>
      </div>
    );
  };

  // Render category badges
  const renderCategories = (categories: Category[]) => {
    return (
      <div className="flex flex-wrap gap-1">
        {categories.map((category) => (
          <span
            key={`${category.id}-${category.name}`}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent-color-100 text-accent-color-800"
          >
            {category.name}
          </span>
        ))}
      </div>
    );
  };

  // Grid columns
  const columns = useMemo(() => {
    const baseColumns = [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (value: string) => (
        <div className="font-medium text-gray-900 dark:text-gray-100">{value}</div>
      )
    },
    {
      id: 'city',
      header: 'City',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-400">{value || '-'}</div>
      )
    },
    {
      id: 'state',
      header: 'State',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-400">{value || '-'}</div>
      )
    },
    {
      id: 'zipCode',
      header: 'Zip Code',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-400">{value || '-'}</div>
      )
    },
    ...(isCoverageSearch
      ? [{
          id: 'distance',
          header: 'Distance',
          sortable: true,
          cell: (value: number) => (
            <div className="text-gray-600 dark:text-gray-400">
              {typeof value === 'number' ? `${value.toFixed(1)} mi` : '-'}
            </div>
          )
        }]
      : []),
    {
      id: 'phone',
      header: 'Phone',
      sortable: true,
      cell: (value: string) => (
        <div className="text-gray-600 dark:text-gray-400">{value ? formatPhoneNumber(value) : '-'}</div>
      )
    },
    {
      id: 'rating',
      header: 'Rating',
      sortable: true,
      cell: (value: number) => (
        <div>{value ? renderRating(value) : '-'}</div>
      )
    },
    {
      id: 'complianceStatusLabel',
      header: '',
      sortable: false,
      stickyWidth: '48px',
      cell: (_value: string, row: Vendor) => (
        <div className="flex justify-center">
          <VendorComplianceIcon
            vendor={row}
            onClick={(e) => {
              e.stopPropagation();
              handleCompliance(row);
            }}
          />
        </div>
      )
    },
    {
      id: 'categories',
      header: 'Categories',
      sortable: false,
      cell: (value: Category[]) => (
        <div>{value && value.length > 0 ? renderCategories(value) : '-'}</div>
      )
    },
    {
      id: 'isActive',
      header: 'Status',
      sortable: true,
      cell: (value: boolean, row: Vendor) => (
        <div className="relative">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              value 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {value ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            {hasVendorCompliance && (
              <button 
                onClick={() => handleCompliance(row)}
                className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                title="Manage Compliance"
              >
                <Shield className="h-4 w-4"/>
              </button>
            )}
            <button 
              onClick={() => navigate(`/vendors/${row.id}`)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="View Details"
            >
              <Eye className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleEdit(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit Vendor"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleDelete(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
              title="Delete Vendor"
            >
              <Trash2 className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )
    }
    ];

    return hasVendorCompliance
      ? baseColumns
      : baseColumns.filter((column) => column.id !== 'complianceStatusLabel');
  }, [handleEdit, handleDelete, handleCompliance, navigate, hasVendorCompliance, isCoverageSearch]);

  const categories = categoriesResponse?.data?.data || [];
  const coverageVendors = useMemo(() => {
    if (!isCoverageSearch) {
      return [];
    }

    let rows = (coverageResponse?.data || []).map((vendor) => mapCoverageVendor(vendor, categories));

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter((vendor) =>
        vendor.name.toLowerCase().includes(term) ||
        (vendor.city || '').toLowerCase().includes(term) ||
        (vendor.state || '').toLowerCase().includes(term) ||
        (vendor.zipCode || '').toLowerCase().includes(term) ||
        (vendor.phone || '').includes(term)
      );
    }

    if (hasVendorCompliance && complianceFilter !== 'all') {
      rows = rows.filter((vendor) => matchesCoverageCompliance(vendor, complianceFilter));
    }

    return sortCoverageVendors(rows, sortField, sortDirection);
  }, [
    isCoverageSearch,
    coverageResponse?.data,
    categories,
    searchTerm,
    hasVendorCompliance,
    complianceFilter,
    sortField,
    sortDirection
  ]);

  const vendors = isCoverageSearch
    ? coverageVendors.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : vendorsResponse?.data?.data || [];
  const totalCount = isCoverageSearch ? coverageVendors.length : vendorsResponse?.data?.totalCount || 0;
  const totalPages = isCoverageSearch
    ? Math.max(1, Math.ceil(coverageVendors.length / pageSize))
    : vendorsResponse?.data?.totalPages || 1;
  const closestDistance = coverageVendors.reduce<number | undefined>((closest, vendor) => {
    if (typeof vendor.distance !== 'number') {
      return closest;
    }
    if (closest === undefined) {
      return vendor.distance;
    }
    return Math.min(closest, vendor.distance);
  }, undefined);
  const coverageErrorMessage = coverageError ? getCoverageErrorMessage(coverageError) : undefined;

  // Check authentication after all hooks are declared
  if (!token || !user) {
    console.log('Not authenticated, redirecting to login');
    window.location.href = '/login';
    return null;
  }

  // Show system admin message if this is a system admin session
  if (isSystemAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center space-x-2">
              <Building2 className="h-6 w-6 text-gray-900 dark:text-gray-100" />
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Vendors</h1>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage work order vendors</p>
          </div>
        </div>

        {/* System Admin Message */}
        <div className="bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-800 rounded-lg p-6">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-accent-color dark:text-accent-color-400 mr-4" />
            <div>
              <h3 className="text-lg font-medium text-accent-color-900 dark:text-accent-color-100">
                System Administrator Access
              </h3>
              <p className="text-accent-color-700 dark:text-accent-color-300 mt-1">
                You are logged in as a System Administrator. Vendor management is handled at the company level. 
                Use the Companies section to manage vendors for specific companies.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !isCoverageSearch) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 dark:text-red-400 text-lg font-medium">Error loading vendors</div>
          <div className="text-gray-500 dark:text-gray-400 mt-2">Please try again</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center space-x-2">
            <Building2 className="h-6 w-6 text-gray-900 dark:text-gray-100" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Vendors</h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage work order vendors</p>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="btn-primary"
        >
          <Plus size={20} className="mr-2" />
          Add Vendor
        </button>
      </div>

        <VendorCoverageSearch
          zipCode={coverageZip}
          radius={coverageRadius}
          onZipCodeChange={handleCoverageZipChange}
          onRadiusChange={handleCoverageRadiusChange}
          onClear={handleCoverageClear}
          isSearching={isCoverageSearch && isCoverageLoading}
          resultCount={isCoverageSearch && !isCoverageLoading && !coverageError ? coverageVendors.length : undefined}
          closestDistance={closestDistance}
          errorMessage={isCoverageSearch ? coverageErrorMessage : undefined}
          onAddVendor={handleOpenCreateModal}
        >
          {hasVendorCompliance && (
            <div className="w-56">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Compliance
              </label>
              <Select
                value={complianceFilter}
                onValueChange={(value) => {
                  setComplianceFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Compliance status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Compliance Statuses</SelectItem>
                  <SelectItem value="compliant">Compliant</SelectItem>
                  <SelectItem value="expiring">Expiring Soon</SelectItem>
                  <SelectItem value="noncompliant">Non-Compliant</SelectItem>
                  <SelectItem value="pendingreview">Needs Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </VendorCoverageSearch>

      <DataGrid
        data={vendors}
        columns={columns}
        isLoading={isCoverageSearch ? isCoverageLoading : isLoading}
        searchTerm={searchTerm}
        onSearch={handleSearch}
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        searchPlaceholder={isCoverageSearch ? 'Filter results...' : 'Search vendors...'}
        emptyMessage={
          isCoverageSearch
            ? `No vendors found within ${coverageRadius} miles of ${coverageZipDigits}`
            : 'No vendors found'
        }
        onRowClick={(row) => navigate(`/vendors/${(row as Vendor).id}`)}
        rowTooltip="View Vendor"
      />

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormError('');
        }}
        title="Add New Vendor"
        maxWidth="2xl"
      >
        <VendorForm
          categories={categories}
          defaultZipCode={coverageZipDigits || undefined}
          onSubmit={handleCreate}
          isLoading={createVendorMutation.isPending}
          error={formError}
        />
      </Modal>


      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedVendor(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Vendor"
        message={`Are you sure you want to delete "${selectedVendor?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteVendorMutation.isPending}
      />

    </div>
  );
};

export default VendorsPage;
